import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Card, Modal } from '@opendate-app/setlist';
import { toastError } from '../../../shared/toastHelper';
import '../styles/clearHold.css';

const HOLD_PROMOTE_NONE = 'HOLD_PROMOTE_NONE';
export const CONFIRM_ARM_DELAY_MS = 500;

const ClearHoldModal = ({ hold, roomNames, hexColor, team, onClose, onConfirm }) => {
  const [submitting, setSubmitting] = useState(false);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const armTimer = setTimeout(() => setArmed(true), CONFIRM_ARM_DELAY_MS);
    return () => clearTimeout(armTimer);
  }, []);

  const holdsMoveUp = team.hold_promotion_level !== HOLD_PROMOTE_NONE;
  const description = holdsMoveUp
    ? 'Holds behind it on this date will move up. This action cannot be undone.'
    : 'This action cannot be undone.';

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = () => {
    if (!armed) return;

    setSubmitting(true);
    onConfirm()
      .catch(() => {
        toastError('Something went wrong. Refresh the calendar to see the latest holds.');
        return false;
      })
      .then((cleared) => {
        setSubmitting(false);
        if (cleared) onClose();
      });
  };

  return (
    <Modal
      open
      onClose={handleClose}
      type="error"
      title="Clear this hold?"
      description={description}
      closeOnBackdrop={armed && !submitting}
      closeOnEsc={!submitting}
      showClose={!submitting}
      footer={(
        <>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Clearing…' : 'Clear hold'}
          </Button>
        </>
      )}
    >
      <Card>
        <div className="clear-hold-summary d-flex align-items-center">
          <span className="clear-hold-position setlist-text-xs setlist-font-bold" style={{ borderColor: hexColor }}>
            {'H' + hold.position}
          </span>
          <div className="clear-hold-details">
            <div className="clear-hold-title setlist-text-sm setlist-font-semibold text-truncate">
              {hold.title}
            </div>
            <div className="clear-hold-meta setlist-text-xs text-truncate">
              {[moment(hold.start).format('ddd, MMM D, YYYY'), roomNames.join(', ')].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
      </Card>
    </Modal>
  );
};

ClearHoldModal.propTypes = {
  hold: PropTypes.shape({
    position: PropTypes.number.isRequired,
    title: PropTypes.string,
    start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]).isRequired,
  }).isRequired,
  roomNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  hexColor: PropTypes.string,
  team: PropTypes.shape({
    hold_promotion_level: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ClearHoldModal;
